import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import type {NodeClass, NodeClassImpl} from "#template/runtime-graph/types"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"

const TD = TypeDescriptors

// cached by the (input, output) descriptor pair to avoid JIT cache pollution from redundant class definitions
const pureLambdaClassCache = new Map<TypeDescriptors.Desc<any>, Map<TypeDescriptors.Desc<any>, any>>()

export function generatePureLambda<I, O>(descriptor?: {input?: TypeDescriptors.Desc<I>; output?: TypeDescriptors.Desc<O>}) {
    const inputDesc = descriptor?.input
    const outputDesc = descriptor?.output

    let byOutput = pureLambdaClassCache.get(inputDesc)
    if (!byOutput) {
        byOutput = new Map()
        pureLambdaClassCache.set(inputDesc, byOutput)
    }
    const existing = byOutput.get(outputDesc)
    if (existing) return existing as typeof PureLambda

    const pureLambdaDescriptor = {
        input: TD.inlet(inputDesc ?? TD.Identity<I>()),
        fn: TD.inlet(TD.StaticFunction<(x: any) => any>()),
        output: TD.outlet(outputDesc ?? TD.Identity<O>()),
    }

    class PureLambda implements NodeClassImpl<typeof pureLambdaDescriptor, NodeClass<typeof pureLambdaDescriptor>> {
        static descriptor = pureLambdaDescriptor
        static uniqueName = "PureLambda"
        input!: Inlet<I>
        fn!: Inlet<(x: I) => O>
        output!: Outlet<O>

        run() {
            if (this.input === NotReady || this.fn === NotReady) {
                this.output.emitIfChanged(NotReady)
                return
            }
            this.output.emitIfChanged(this.fn(this.input))
        }
    }

    byOutput.set(outputDesc, PureLambda)
    return PureLambda
}
